import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Lock } from 'lucide-react';

// Two-step "check your territory" quick form for the tree-service landing page.
// Step 1: city. Step 2: contact. Captures to Meta CAPI + GHL (same wiring as
// EligibilityQuiz), then routes to /thank-you. Firma monochrome styling.
export default function TreeQuickForm({ id }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [city, setCity] = useState('');
  const [contact, setContact] = useState({ firstName: '', email: '', phone: '+1 ', company: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onContact = (e) => setContact({ ...contact, [e.target.name]: e.target.value });

  const next = () => {
    if (!city.trim()) {
      setErrors({ city: 'Enter your city' });
      return;
    }
    setErrors({});
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = {};
    if (!contact.firstName.trim()) errs.firstName = 'Enter your first name';
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) errs.email = 'Enter a valid email';
    if (contact.phone.replace(/\D/g, '').length < 7) errs.phone = 'Enter a valid phone number';
    if (!contact.company.trim()) errs.company = 'Enter your company';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsSubmitting(true);

    const eventId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `lead_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'Tree Territory Form' }, { eventID: eventId });
    }

    const customData = { city: city.trim(), company: contact.company.trim(), pipeline: 'main' };

    const capi = fetch('/api/meta/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: 'Lead',
        event_id: eventId,
        event_source_url: window.location.href,
        email: contact.email,
        phone: contact.phone,
        first_name: contact.firstName,
        custom_data: customData,
      }),
    });

    const ghl = fetch('/api/ghl-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: contact.firstName,
        first_name: contact.firstName,
        email: contact.email,
        phone: contact.phone,
        city: city.trim(),
        company: contact.company.trim(),
        pipeline: 'main',
        tags: ['website-lead', 'tree-service', 'territory-check'],
        source: 'firmamkt.com tree-service',
        event_id: eventId,
      }),
    });

    await Promise.allSettled([capi, ghl]);

    setIsSubmitting(false);
    navigate('/thank-you', {
      state: { name: contact.firstName, email: contact.email, phone: contact.phone, pipeline: 'main' },
    });
  };

  const inputClass =
    'w-full px-4 py-3 rounded-lg border border-[#D8D2C4] bg-[#FBFAF6] text-[#22301F] placeholder-[#A7A190] focus:outline-none focus:border-[#5E7D3A] focus:ring-1 focus:ring-[#5E7D3A]';

  return (
    <div id={id} className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_30px_70px_-20px_rgba(15,26,18,0.55)] w-full max-w-md border border-[#EAE4D6]">
      <h3 className="font-display text-center text-xl font-bold text-[#22301F] mb-4">
        Check If Your Territory Is Still Available
      </h3>

      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8474] mb-2">
        Step {step + 1} of 2
      </p>
      <div className="h-1.5 bg-[#E9E3D5] rounded-full mb-6 overflow-hidden">
        <div
          className="h-1.5 bg-[#5E7D3A] rounded-full transition-all duration-300"
          style={{ width: step === 0 ? '50%' : '100%' }}
        />
      </div>

      {step === 0 ? (
        <div>
          <label className="block text-sm font-semibold text-[#22301F] mb-2">
            What city is your company located in? *
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && next()}
            placeholder="e.g. Miami, Denver, Chicago"
            className={inputClass}
            autoFocus
            data-testid="tf-city"
          />
          {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}

          <button
            type="button"
            onClick={next}
            className="btn-moss w-full mt-5 flex items-center justify-center gap-2"
            data-testid="tf-next"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <button
            type="button"
            onClick={() => setStep(0)}
            className="text-xs text-[#8A8474] hover:text-[#22301F] flex items-center gap-1 mb-4"
            data-testid="tf-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#22301F] mb-1.5">First Name *</label>
              <input name="firstName" value={contact.firstName} onChange={onContact} placeholder="John" className={inputClass} autoComplete="given-name" data-testid="tf-firstName" />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#22301F] mb-1.5">Email *</label>
              <input name="email" type="email" value={contact.email} onChange={onContact} placeholder="john@company.com" className={inputClass} autoComplete="email" data-testid="tf-email" />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#22301F] mb-1.5">Phone Number *</label>
              <input name="phone" type="tel" value={contact.phone} onChange={onContact} placeholder="+1 (555) 000-0000" className={inputClass} autoComplete="tel" data-testid="tf-phone" />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#22301F] mb-1.5">Company *</label>
              <input name="company" value={contact.company} onChange={onContact} placeholder="Expert Tree Care" className={inputClass} autoComplete="organization" data-testid="tf-company" />
              {errors.company && <p className="text-xs text-red-600 mt-1">{errors.company}</p>}
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-moss w-full mt-1" data-testid="tf-submit">
              {isSubmitting ? 'Checking…' : 'Check My Territory'}
            </button>
          </div>
        </form>
      )}

      <p className="text-xs text-[#8A8474] text-center mt-5 flex items-center justify-center gap-1.5">
        <Lock className="w-3 h-3" /> Your info is secure. We only review your market and reach out if it's open.
      </p>
    </div>
  );
}
